import { useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2, FileImage, X, BadgeCheck } from "lucide-react";

const ACCEPTED = "image/png,image/jpeg,image/jpg,image/webp";
const MAX_BYTES = 8 * 1024 * 1024;

export default function BillUploader({ onApply }) {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [result, setResult] = useState(null);
    const [drag, setDrag] = useState(false);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setResult(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleFile = (f) => {
        if (!f) return;
        if (!ACCEPTED.split(",").includes(f.type)) {
            toast.error("Format non supporté. Utilisez PNG, JPEG ou WEBP.");
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error("Fichier trop volumineux (max 8 Mo).");
            return;
        }
        setFile(f);
        setResult(null);
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(f);
    };

    const analyze = async () => {
        if (!file) return;
        setAnalyzing(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/bills/analyze", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            setResult(data);
            if (data.error) {
                toast.error("Analyse partielle — voir détails");
            } else {
                toast.success("Facture analysée");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setAnalyzing(false);
        }
    };

    const apply = () => {
        if (result && onApply) {
            onApply(result);
            toast.success("Données du simulateur pré-remplies");
            reset();
        }
    };

    const confidenceColor = {
        high: "bg-emerald-500",
        medium: "bg-amber-500",
        low: "bg-rose-500",
    };

    return (
        <Card className="rounded-3xl border-border bg-card overflow-hidden" data-testid="bill-uploader">
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="em-eyebrow flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />Analyse de facture</p>
                        <h3 className="font-display text-lg font-bold text-primary mt-1">Préremplissez en 1 clic.</h3>
                    </div>
                    {file && (
                        <Button variant="ghost" size="sm" type="button" onClick={reset} data-testid="bill-clear" className="rounded-full">
                            <X className="h-4 w-4 mr-1" />Effacer
                        </Button>
                    )}
                </div>

                {!file && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                        onDragLeave={() => setDrag(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDrag(false);
                            handleFile(e.dataTransfer.files?.[0]);
                        }}
                        className={`w-full rounded-2xl border-2 border-dashed transition-all p-8 text-center ${
                            drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                        }`}
                        data-testid="bill-dropzone"
                    >
                        <Upload className="h-10 w-10 mx-auto text-primary/50" />
                        <p className="font-display font-bold text-primary mt-3">Glissez votre facture ici</p>
                        <p className="text-sm text-muted-foreground mt-1">ou cliquez pour parcourir (PNG, JPEG, WEBP · 8 Mo max)</p>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={ACCEPTED}
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            className="hidden"
                            data-testid="bill-input"
                        />
                    </button>
                )}

                {file && (
                    <div className="space-y-4">
                        <div className="flex gap-4 items-start">
                            {preview && (
                                <img src={preview} alt="Aperçu" className="h-28 w-28 rounded-xl object-cover border border-border" data-testid="bill-preview" />
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-display font-bold text-primary truncate">{file.name}</p>
                                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} Ko · {file.type}</p>
                                <Button
                                    onClick={analyze}
                                    disabled={analyzing}
                                    type="button"
                                    size="sm"
                                    className="rounded-full mt-3 bg-primary hover:bg-primary/90"
                                    data-testid="bill-analyze"
                                >
                                    {analyzing ? (
                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse en cours…</>
                                    ) : (
                                        <><Sparkles className="h-4 w-4 mr-2" />Analyser la facture</>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {result && (
                            <div className="border-t border-border pt-4 space-y-3" data-testid="bill-result">
                                <div className="flex items-center justify-between">
                                    <p className="em-eyebrow">Données extraites</p>
                                    <Badge className={`text-white ${confidenceColor[result.confidence] || "bg-muted"}`}>
                                        Confiance {result.confidence}
                                    </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Field label="Fournisseur" value={result.supplier_name} />
                                    <Field label="Énergie" value={result.energy_type === "gas" ? "Gaz" : result.energy_type === "electricity" ? "Électricité" : "—"} />
                                    <Field label="Conso annuelle" value={result.annual_kwh ? `${Math.round(result.annual_kwh)} kWh` : "—"} />
                                    <Field label="Prix kWh" value={result.current_price_kwh ? `${Number(result.current_price_kwh).toFixed(4)} €` : "—"} />
                                    <Field label="Abonnement / mois" value={result.current_subscription_monthly ? `${Number(result.current_subscription_monthly).toFixed(2)} €` : "—"} />
                                </div>
                                {result.notes && (
                                    <p className="text-xs text-muted-foreground italic">{result.notes}</p>
                                )}
                                <Button
                                    onClick={apply}
                                    type="button"
                                    disabled={!result.annual_kwh && !result.current_price_kwh}
                                    className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                                    data-testid="bill-apply"
                                >
                                    <BadgeCheck className="h-4 w-4 mr-2" />
                                    Utiliser ces données dans le simulateur
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function Field({ label, value }) {
    return (
        <div className="bg-secondary rounded-xl p-3">
            <p className="em-eyebrow">{label}</p>
            <p className="font-mono font-bold text-primary mt-0.5">{value || "—"}</p>
        </div>
    );
}
